import { formatMoney } from '@theme/money-formatting';

const SECTION_ID = 'oppie-upsell-quick-view';
const CLOSE_MS = 240;

/** Opgehaalde snelkijk-HTML per product-URL, zodat heen en weer klikken direct is. */
const cache = new Map();

/** @param {string} url */
function fetchQuickView(url) {
  if (!cache.has(url)) {
    const request = fetch(`${url}${url.includes('?') ? '&' : '?'}section_id=${SECTION_ID}`)
      .then((response) => {
        if (!response.ok) throw new Error(`Snelkijk ${response.status}`);
        return response.text();
      })
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const content = doc.querySelector('[data-quick-view-content]');
        if (!content) throw new Error('Snelkijk zonder inhoud');
        return content.outerHTML;
      })
      .catch((error) => {
        cache.delete(url); // volgende klik mag het opnieuw proberen
        throw error;
      });
    cache.set(url, request);
  }
  return cache.get(url);
}

/**
 * <oppie-upsell-quick-view>, de snelkijk bij de PDP-upsell.
 *
 * Klik op foto of naam van een gratis-rij of add-on ([data-upsell-quickview])
 * opent een dialoog met de productinfo, live opgehaald via de Section Rendering
 * API. De knoppen onderin hebben zelf geen stand: ze klikken op de plus, min of
 * "Voeg toe" van de rij in de upsell, en lezen daarna het aantal en de
 * prijsregel terug. Zo blijft oppie-pdp-upsell.js de enige die rekent.
 */
class OppieUpsellQuickView extends HTMLElement {
  connectedCallback() {
    this.host = this.closest('oppie-pdp-upsell') || this.parentElement;
    this.dialog = this.querySelector('[data-quick-view-dialog]');
    this.target = this.querySelector('[data-quick-view-target]');
    this.skeleton = this.querySelector('[data-quick-view-skeleton]');
    this.status = this.querySelector('[data-quick-view-status]');
    this.addButton = this.querySelector('[data-quick-view-add]');
    this.stepper = this.querySelector('[data-quick-view-stepper]');
    this.value = this.querySelector('[data-quick-view-value]');
    this.minus = this.querySelector('[data-quick-view-minus]');
    this.doneButton = this.querySelector('[data-quick-view-done]');
    if (!this.host || !this.dialog) return;

    this.plus = this.querySelector('[data-quick-view-plus]');
    this.observer = new MutationObserver(() => this.#syncFooter());

    // Een signaal voor alle listeners, zodat opnieuw inhangen (theme editor,
    // morph) ze niet verdubbelt.
    this.abort = new AbortController();
    const signal = this.abort.signal;

    this.host.addEventListener('click', this.#onTriggerClick, { signal });
    this.host.addEventListener('pointerenter', this.#onPrefetch, { capture: true, signal });
    this.host.addEventListener('focusin', this.#onPrefetch, { signal });

    this.querySelector('[data-quick-view-close]')?.addEventListener('click', () => this.close(), { signal });
    this.doneButton?.addEventListener('click', () => this.close(), { signal });
    this.addButton?.addEventListener(
      'click',
      () => this.#pressSource(this.#isAddon() ? '[data-upsell-addon-add]' : '[data-upsell-plus]'),
      { signal }
    );
    this.minus?.addEventListener('click', () => this.#pressSource('[data-upsell-minus]'), { signal });
    this.plus?.addEventListener('click', () => this.#pressSource('[data-upsell-plus]'), { signal });

    // Klik op de achtergrond (buiten het paneel) sluit, net als Esc.
    this.dialog.addEventListener(
      'click',
      (event) => {
        if (event.target === this.dialog) this.close();
      },
      { signal }
    );
    this.dialog.addEventListener(
      'cancel',
      (event) => {
        event.preventDefault();
        this.close();
      },
      { signal }
    );
    this.target?.addEventListener('scroll', this.#onSlideScroll, { capture: true, passive: true, signal });
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.abort?.abort();
  }

  #onTriggerClick = (event) => {
    const trigger = event.target instanceof Element ? event.target.closest('[data-upsell-quickview]') : null;
    if (!trigger || !this.host.contains(trigger)) return;
    event.preventDefault();
    this.open(trigger);
  };

  #onPrefetch = (event) => {
    const trigger = event.target instanceof Element ? event.target.closest('[data-upsell-quickview]') : null;
    if (trigger?.dataset.url) fetchQuickView(trigger.dataset.url).catch(() => {});
  };

  /** @param {HTMLElement} trigger */
  async open(trigger) {
    const url = trigger.dataset.url;
    if (!url) return;
    this.source = trigger.closest('[data-upsell-row], [data-upsell-addon]');
    this.returnFocus = trigger;
    const name = this.source?.querySelector('.oppie-upsell-row__name, .oppie-upsell-addon__name')?.textContent.trim();
    if (name) this.dialog.setAttribute('aria-label', name);

    this.observer.disconnect();
    if (this.source) this.observer.observe(this.source, { attributes: true, attributeFilter: ['data-qty'], subtree: true, childList: true, characterData: true });
    this.#syncFooter();

    this.#showSkeleton();
    if (!this.dialog.open) {
      this.dialog.showModal();
      document.documentElement.classList.add('oppie-quick-view-lock');
      requestAnimationFrame(() => this.dialog.classList.add('is-open'));
    }

    try {
      const html = await fetchQuickView(url);
      if (this.returnFocus !== trigger) return; // intussen een andere geopend
      this.target.innerHTML = html;
      this.target.removeAttribute('aria-busy');
      this.target.scrollTop = 0;
    } catch (error) {
      console.error('Oppie snelkijk laden mislukt:', error);
      if (this.returnFocus !== trigger) return;
      this.#showError(url);
    }
  }

  close() {
    if (!this.dialog.open || this.closing) return;
    this.closing = true;
    this.dialog.classList.remove('is-open');
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.setTimeout(() => {
      this.dialog.close();
      this.closing = false;
      document.documentElement.classList.remove('oppie-quick-view-lock');
      this.observer.disconnect();
      this.returnFocus?.focus({ preventScroll: true });
    }, reduce ? 0 : CLOSE_MS);
  }

  #showSkeleton() {
    this.target.setAttribute('aria-busy', 'true');
    this.target.replaceChildren(this.skeleton.content.cloneNode(true));
  }

  #showError(url) {
    this.target.removeAttribute('aria-busy');
    const box = document.createElement('div');
    box.className = 'oppie-quick-view__error';
    const text = document.createElement('p');
    text.textContent = this.dataset.error || '';
    const link = document.createElement('a');
    link.className = 'oppie-quick-view__more';
    link.href = url;
    link.textContent = this.dataset.more || '';
    box.append(text, link);
    this.target.replaceChildren(box);
  }

  #isAddon() {
    return Boolean(this.source?.hasAttribute('data-upsell-addon'));
  }

  #qty() {
    return parseInt(this.source?.dataset.qty || '0', 10) || 0;
  }

  /** Klikt de bijbehorende knop in de upsell, die regelt aantal en prijs. */
  #pressSource(selector) {
    const button = this.source?.querySelector(selector);
    if (!button || button.disabled) return;
    button.click();
    this.#syncFooter();
    // "Voeg toe" verdwijnt zodra er een aantal staat: zet de focus dan op de plus.
    const focused = document.activeElement;
    if (!focused || !this.dialog.contains(focused) || focused.closest('[hidden]')) {
      (this.#qty() > 0 ? this.plus : this.addButton)?.focus();
    }
  }

  #syncFooter() {
    if (!this.source) return;
    const qty = this.#qty();
    const active = qty > 0;
    this.addButton.hidden = active;
    this.stepper.hidden = !active;
    this.doneButton.hidden = !active;
    this.value.textContent = String(qty);
    this.minus.disabled = qty <= 0;
    this.plus.disabled = Boolean(this.source.querySelector('[data-upsell-plus]')?.disabled);

    const priceLine = this.#isAddon()
      ? this.source.querySelector('.oppie-upsell-addon__now')
      : this.source.querySelector('[data-upsell-row-price]');
    let text = priceLine?.textContent.trim() || '';
    let state = priceLine?.dataset.state || (active ? 'active' : '');
    // Gratis mix op 0: laat zien wat "Voeg toe" kost, zodat de klant weet waar hij aan toe is.
    if (!text && !this.#isAddon()) {
      const freeLeft = !this.host.querySelector('[data-upsell-counter]')?.classList.contains('is-complete');
      text = freeLeft ? this.host.dataset.labelFree || '' : this.#price();
      state = freeLeft ? 'free' : '';
    }
    this.status.textContent = text;
    this.status.dataset.state = state;
  }

  #price() {
    const cents = parseInt(this.source?.dataset.price || '0', 10) || 0;
    const format = this.host.dataset.moneyFormat || '€{{amount_with_comma_separator}}';
    return `+ ${formatMoney(cents, format, this.host.dataset.currency || 'EUR')}`;
  }

  #onSlideScroll = (event) => {
    const slides = event.target;
    if (!(slides instanceof HTMLElement) || !slides.matches('[data-quick-view-slides]')) return;
    const index = Math.round(slides.scrollLeft / Math.max(1, slides.clientWidth));
    this.target.querySelectorAll('[data-quick-view-dot]').forEach((dot, i) => {
      dot.classList.toggle('is-active', i === index);
    });
  };
}

if (!customElements.get('oppie-upsell-quick-view')) {
  customElements.define('oppie-upsell-quick-view', OppieUpsellQuickView);
}
